/* eslint-disable no-unused-vars */
import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import './AuthModal.css'

// Lightweight GA4 event helper — no-ops if gtag isn't loaded
function track(event, params = {}) {
    if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', event, params)
    }
}

function AuthModal({ onClose }) {
    const { signIn, signUp } = useAuth()
    const [isSignUp, setIsSignUp] = useState(false)
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [displayName, setDisplayName] = useState('')
    const [error, setError] = useState('')
    const [message, setMessage] = useState('')
    const [loading, setLoading] = useState(false)

    // Modal only renders for logged-out users hitting the auth wall
    useEffect(() => {
        track('signup_prompted')
    }, [])

    const handleSubmit = async (e) => {
        e.preventDefault()
        setError('')
        setMessage('')
        setLoading(true)

        try {
            if (isSignUp) {
                // Check email exists
                const checkRes = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/check-email?email=${encodeURIComponent(email)}`)
                const checkData = await checkRes.json()
                if (checkData.exists) {
                    setError('An account with this email already exists. Try signing in instead.')
                    setLoading(false)
                    return
                }
                // Check display name availability
                if (displayName.trim()) {
                    const nameRes = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/check-display-name?display_name=${encodeURIComponent(displayName.trim())}`)
                    const nameData = await nameRes.json()
                    if (nameData.taken) {
                        setError('That display name is already taken. Please choose another.')
                        setLoading(false)
                        return
                    }
                }
                const { data, error } = await signUp(email, password, displayName.trim())
                if (error) throw error
                if (data?.user && data.user.identities && data.user.identities.length === 0) {
                    setError('An account with this email already exists. Try signing in instead.')
                    setLoading(false)
                    return
                }
                setMessage('Check your email to confirm your account.')
            } else {
                const { error } = await signIn(email, password)
                if (error) throw error
                onClose()
            }
        } catch (err) {
            setError(err.message)
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{isSignUp ? 'Create account' : 'Sign in'}</h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Email</label>
                        <input
                            type="email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            required
                        />
                    </div>
                    {isSignUp && (
                        <div className="form-group">
                            <label>Display name <span className="optional-label">(optional)</span></label>
                            <input
                                type="text"
                                value={displayName}
                                onChange={e => setDisplayName(e.target.value)}
                                placeholder="e.g. John Smith"
                                maxLength={30}
                            />
                        </div>
                    )}
                    <div className="form-group">
                        <label>Password</label>
                        <input
                            type="password"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            required
                        />
                    </div>
                    {error && <p className="error">{error}</p>}
                    {message && <p className="success">{message}</p>}
                    <button type="submit" className="btn-primary full-width" disabled={loading}>
                        {loading ? 'Loading...' : isSignUp ? 'Create account' : 'Sign in'}
                    </button>
                </form>
                {!isSignUp && (
                    <a href="/forgot-password" className="forgot-password-link" onClick={onClose}>
                        Forgot your password?
                    </a>
                )}
                <p className="auth-switch">
                    {isSignUp ? 'Already have an account?' : "Don't have an account?"}
                    <button onClick={() => setIsSignUp(!isSignUp)}>
                        {isSignUp ? 'Sign in' : 'Sign up'}
                    </button>
                </p>
            </div>
        </div>
    )
}

export default AuthModal
